import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import type { BorderSide, RuntimePropsWithChildren } from "./shared";
import {
  ASSET_BASE,
  CARD_HOVER_PROPS,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  getGridClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { IconChild } from "./iconChild";
import { ImageChild } from "./imageChild";
import { type WBReveal } from "./reveal";
import { AttachmentChips } from "./attachmentChips";
import { CustomFieldLines } from "./customFieldLines";

const SPEAKER_DEFAULT_ICONS = [
  "IconGlobe",
  "IconMail",
  "IconPhone",
  "IconBrandLinkedin",
  "IconBrandInstagram",
  "IconBrandTwitter",
];

export function SpeakerChild({
  isVisible = true,
  isVisibleName,
  textName,
  fontSizeName = "text-lg",
  fontFamilyName,
  textAlignName = "left",
  fontColorName,
  isVisibleOccupation,
  textOccupation,
  fontSizeOccupation = "text-md",
  fontFamilyOccupation,
  textAlignOccupation = "left",
  fontColorOccupation,
  backgroundColor = "transparent",
  borderRadius,
  border = false,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides = [],
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distImageContent = "gap-3",
  distTexts = "gap-2",
  iconCount = 3,
  attachments,
  customFields,
}: {
  isVisible?: boolean;
  isVisibleName?: boolean;
  textName?: string;
  fontSizeName?: string;
  fontFamilyName?: string;
  textAlignName?: "left" | "center" | "right" | "justify";
  fontColorName?: string;
  isVisibleOccupation?: boolean;
  textOccupation?: string;
  fontSizeOccupation?: string;
  fontFamilyOccupation?: string;
  textAlignOccupation?: "left" | "center" | "right" | "justify";
  fontColorOccupation?: string;
  backgroundColor?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
  padding?: string;
  distImageContent?: string;
  distTexts?: string;
  iconCount?: number;
  attachments?: unknown;
  /** Baked PUBLISHED custom fields ({label, value}[]). Only definitions the
   *  organiser marked `publicOnWebsite` are baked, and the speaker's own
   *  visibility toggle can still drop one. Older per-event snapshots simply
   *  never carry the prop. */
  customFields?: unknown;
}) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  if (!isVisible) return null;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  return (
    <motion.div
      {...CARD_HOVER_PROPS}
      className={classNames(
        "w-full h-full flex flex-col",
        padding,
        distImageContent,
        borderClasses,
        roundedClasses,
      )}
      style={{
        transition:
          "background-color 300ms ease-in-out, border-color 300ms ease-in-out",
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      <Element
        is={ImageChild}
        id="image"
        // Creation-time default for the linked image node (a stored page
        // brings its own src). Anonymous silhouette on purpose: this card may
        // carry a REAL speaker's name, and a demo portrait under it reads as
        // that person's photo. MIRRORS the editor twin's
        // SPEAKER_PLACEHOLDER_IMAGE_URL (main-frontend children/Speaker).
        src={`${ASSET_BASE}/shared/samples/speaker-neutral.svg`}
        aspectRatio="aspect-square"
        objectFit="object-cover"
        objectPosition="object-center"
        rounded="rounded-lg"
      />
      <div className={classNames("flex flex-col w-full", distTexts)}>
        <TextChild
          isVisible={isVisibleName}
          text={textName}
          fontSize={fontSizeName}
          fontFamily={fontFamilyName}
          textAlign={textAlignName}
          color={fontColorName}
          headingLevel="h2"
        />
        <TextChild
          isVisible={isVisibleOccupation}
          text={textOccupation}
          fontSize={fontSizeOccupation}
          fontFamily={fontFamilyOccupation}
          textAlign={textAlignOccupation}
          color={fontColorOccupation}
        />
        <div className="flex">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={`icon-${i}`}
              style={i >= (iconCount ?? 3) ? { display: "none" } : undefined}
            >
              <Element
                is={IconChild}
                id={`icon-${i}`}
                iconName={SPEAKER_DEFAULT_ICONS[i] ?? "IconPhone"}
                iconSize="medium"
                showLinkSettings
                href=""
              />
            </div>
          ))}
        </div>
        {/* Published custom fields — mirror of the editor twin. */}
        <CustomFieldLines customFields={customFields} />
        {/* Public attachments (Wave A) — baked, showOnWebsite-filtered. */}
        <AttachmentChips attachments={attachments} className="mt-2" />
      </div>
    </motion.div>
  );
}

export function Speaker(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextSpeakers =
    (props.distTextSpeakers as string | undefined) ?? "gap-3";
  const distSpeakers = (props.distSpeakers as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "3";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col w-full",
          alignmentClass,
          (props.isVisibleHeadline as boolean | undefined) ||
            (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextSpeakers,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div
            className={classNames(
              getGridClasses(gridColumns, layoutWidth),
              distSpeakers,
              rightWidth,
            )}
          >
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
